import { type ReactNode, useCallback, useMemo } from "react";
import { ArrowDownIcon } from "../../icons";
import type { Optional } from "../../types";
import styles from "./accordion-header.module.css";
import { useAccordionItem, useAccordionTransitionEnabled } from "./hooks";

export type AccordionHeaderProps = {
  children: ReactNode;
  hideArrowDown?: Optional<boolean>;
  color?: string;
  onClose?: (isOpen: boolean) => void;
  onOpen?: (isOpen: boolean) => void;
} & Omit<React.AllHTMLAttributes<HTMLButtonElement>, "type">;

export function AccordionHeader({
  hideArrowDown,
  children,
  color,
  onClose,
  onOpen,
  ...props
}: AccordionHeaderProps) {
  const { handleOpen, handleClose, isOpen, openedByDefault } =
    useAccordionItem();
  const iconTransitionEnabled = useAccordionTransitionEnabled(openedByDefault);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      onClose?.(isOpen);
      return handleClose();
    }

    onOpen?.(isOpen);
    return handleOpen();
  }, [isOpen, handleClose, handleOpen, onClose, onOpen]);

  const iconClassName = useMemo(() => {
    const parts = [styles.Icon];

    if (isOpen) {
      parts.push(styles.IsOpen);
    }

    if (!iconTransitionEnabled) {
      parts.push(styles.IconSkipInitialTransition);
    }

    return parts.join(" ");
  }, [isOpen, iconTransitionEnabled]);

  return (
    <button
      type="button"
      className={styles.Main}
      onClick={handleToggle}
      {...props}
    >
      <div className={styles.Title}>{children}</div>
      {!hideArrowDown && (
        <div className={iconClassName}>
          <ArrowDownIcon color={color} size={24} />
        </div>
      )}
    </button>
  );
}
